import { createSlice } from '@reduxjs/toolkit';
import { IExternalSenseiDashboard } from '../types';
import { DPD_BUCKET_OPTIONS } from '../constants';

const initialState: IExternalSenseiDashboard = {
  dpdBucketTable: {
    dpdBucketData: {},
    loading: false
  },
  agentPerformanceTlTable: {
    agentPerformanceTlData: {},
    loading: false
  },
  agencyDetailsTable: {
    agencyDetailsMtdData: {},
    agencyDetailsTodayData: {},
    loading: false
  },
  agentPerformanceTable: {
    agentPerformanceMtdData: {},
    agentPerformanceTodayData: {},
    loading: false
  },
  agencyDetailsTeleGovernanceTable: {
    agencyDetailsMtdData: {},
    agencyDetailsTodayData: {},
    loading: false
  },
  agentPerformanceTeleGovernanceTable: {
    agentPerformanceMtdData: {},
    agentPerformanceTodayData: {},
    loading: false
  },
  agencyManagerPerformanceTable: {
    agencyManagerPerformanceData: {},
    loading: false
  },
  agencyPerformanceTable: {
    agencyPerformanceData: {},
    loading: false
  },
  CCMRMOverallPerformanceExternalFilters: {
    schema: {
      state: {
        options: [],
        multiSelect: true,
        label: 'State'
      },
      agencyManager: {
        options: [],
        multiSelect: true,
        label: 'Agency Manager'
      },
      agency: {
        options: [],
        multiSelect: true,
        label: 'Agency'
      },
      dpdBucket: {
        options: DPD_BUCKET_OPTIONS,
        multiSelect: true,
        label: 'Bucket'
      }
    }
  },
  agencyNameOptions: [],
  stateOptions: [],
  agentsOptions: [],
  allLastUpdatedAt: {
    dpdBucketLastUpdatedAt: 0,
    agencyDetailsLastUpdatedAt: 0,
    agentPerformanceLastUpdatedAt: 0,
    agencyDetailsTeleGovernanceLastUpdatedAt: 0,
    agentPerformanceTeleGovernanceLastUpdatedAt: 0,
    agentPerformanceTlLastUpdatedAt: 0,
    agencyManagerPerformanceLastUpdatedAt: 0,
    agencyPerformanceLastUpdatedAt: 0,
    allocationViewLastUpdatedAt: 0
  },
  achievementMovementData: {
    aggregationLevel: '',
    pages: {
      pageNo: 0,
      totalPages: 10
    },
    hourlyData: [],
    dailyData: [],
    filterOptions: {
      availableFPRs: [],
      availableADMs: [],
      availableAgencies: null
    }
  },
  isLoadingAchievementMovementData: false,
  genuineFieldCoverageData: {
    aggregationLevel: '',
    pages: {
      pageNo: 0,
      totalPages: 10
    },
    dailyData: [],
    filterOptions: {
      availableFPRs: [],
      availableADMs: [],
      availableAgencies: null
    }
  },
  isLoadingGenuineFieldCoverageData: false
};

const externalDashboardSenseiSlice = createSlice({
  name: 'externalDashboardSensei',
  initialState,
  reducers: {
    setDpdBucketTable: (state, action) => {
      const { data } = action.payload;
      state.dpdBucketTable.dpdBucketData = data;
    },
    setLoadingDpdBucket: (state, action) => {
      state.dpdBucketTable.loading = action.payload;
    },
    setAgentPerformanceTlTable: (state, action) => {
      const { data } = action.payload;
      state.agentPerformanceTlTable.agentPerformanceTlData = data;
    },
    setLoadingAgentPerformanceTl: (state, action) => {
      state.agentPerformanceTlTable.loading = action.payload;
    },
    setAgencyDetailsTable: (state, action) => {
      const { data, isToday } = action.payload;
      if (isToday) {
        state.agencyDetailsTable.agencyDetailsTodayData = data;
        return;
      }
      state.agencyDetailsTable.agencyDetailsMtdData = data;
    },
    setLoadingAgencyDetails: (state, action) => {
      state.agencyDetailsTable.loading = action.payload;
    },
    setAgentPerformanceTable: (state, action) => {
      const { data, isToday } = action.payload;
      if (isToday) {
        state.agentPerformanceTable.agentPerformanceTodayData = data;
        return;
      }
      state.agentPerformanceTable.agentPerformanceMtdData = data;
    },
    setLoadingAgentPerformance: (state, action) => {
      state.agentPerformanceTable.loading = action.payload;
    },
    setAgencyDetailsTeleGovernanceTable: (state, action) => {
      const { data, isToday } = action.payload;
      if (isToday) {
        state.agencyDetailsTeleGovernanceTable.agencyDetailsTodayData = data;
        return;
      }
      state.agencyDetailsTeleGovernanceTable.agencyDetailsMtdData = data;
    },
    setLoadingAgencyTeleGovernanceDetails: (state, action) => {
      state.agencyDetailsTeleGovernanceTable.loading = action.payload;
    },
    setAgentPerformanceTeleGovernanceTable: (state, action) => {
      const { data, isToday } = action.payload;
      if (isToday) {
        state.agentPerformanceTeleGovernanceTable.agentPerformanceTodayData = data;
        return;
      }
      state.agentPerformanceTeleGovernanceTable.agentPerformanceMtdData = data;
    },
    setLoadingAgentTeleGovernancePerformance: (state, action) => {
      state.agentPerformanceTeleGovernanceTable.loading = action.payload;
    },
    setAgencyManagerPerformanceTable: (state, action) => {
      state.agencyManagerPerformanceTable = action.payload;
    },
    setLoadingAgencyManagerPerformance: (state, action) => {
      state.agencyManagerPerformanceTable.loading = action.payload;
    },
    setAgencyPerformanceTable: (state, action) => {
      state.agencyPerformanceTable = action.payload;
    },
    setLoadingAgencyPerformance: (state, action) => {
      state.agencyPerformanceTable.loading = action.payload;
    },
    setSchema(state, action) {
      state.CCMRMOverallPerformanceExternalFilters.schema.state.options = action.payload.states;
      state.CCMRMOverallPerformanceExternalFilters.schema.agencyManager.options =
        action.payload.agencyManagers;
      state.CCMRMOverallPerformanceExternalFilters.schema.agency.options = action.payload.agencies;
      state.CCMRMOverallPerformanceExternalFilters.schema.dpdBucket.options =
        action.payload.dpdBucket;
    },
    setAgencyNameOptions: (state, action) => {
      state.agencyNameOptions = action.payload;
    },
    setStateOptions: (state, action) => {
      state.stateOptions = action.payload;
    },
    setAgentsOptions: (state, action) => {
      state.agentsOptions = action.payload;
    },
    setTablesLastUpdatedAt: (state, action) => {
      state.allLastUpdatedAt = { ...state.allLastUpdatedAt, ...action.payload };
    },
    setAchievementMovementData: (state, action) => {
      state.achievementMovementData = action.payload;
    },
    setLoadingAchievementMovementData: (state, action) => {
      state.isLoadingAchievementMovementData = action.payload;
    },
    setGenuineFieldCoverageData: (state, action) => {
      state.genuineFieldCoverageData = action.payload;
    },
    setLoadingGenuineFieldCoverageData: (state, action) => {
      state.isLoadingGenuineFieldCoverageData = action.payload;
    }
  }
});

export const {
  setDpdBucketTable,
  setLoadingDpdBucket,
  setAgentPerformanceTlTable,
  setLoadingAgentPerformanceTl,
  setAgencyDetailsTable,
  setLoadingAgencyDetails,
  setAgentPerformanceTable,
  setLoadingAgentPerformance,
  setAgencyDetailsTeleGovernanceTable,
  setLoadingAgencyTeleGovernanceDetails,
  setAgentPerformanceTeleGovernanceTable,
  setLoadingAgentTeleGovernancePerformance,
  setAgencyManagerPerformanceTable,
  setLoadingAgencyManagerPerformance,
  setAgencyPerformanceTable,
  setLoadingAgencyPerformance,
  setSchema,
  setAgencyNameOptions,
  setStateOptions,
  setAgentsOptions,
  setTablesLastUpdatedAt,
  setAchievementMovementData,
  setLoadingAchievementMovementData,
  setGenuineFieldCoverageData,
  setLoadingGenuineFieldCoverageData
} = externalDashboardSenseiSlice.actions;

export default externalDashboardSenseiSlice.reducer;
